"use client"

import React, { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { LucideIcon, Menu, X } from "lucide-react"
import { cn } from "@/lib/utils"

interface NavItem {
    name: string
    url: string
    icon: LucideIcon
}

interface NavBarProps {
    items: NavItem[]
    className?: string
    leftElement?: React.ReactNode
    rightElement?: React.ReactNode
}

export function NavBar({ items, className, leftElement, rightElement }: NavBarProps) {
    const [activeTab, setActiveTab] = useState(items[0].name)
    const [isMobile, setIsMobile] = useState(false)

    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768)
        }

        handleResize()
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    return (
        <div
            className={cn(
                "fixed top-0 left-0 right-0 md:left-1/2 md:-translate-x-1/2 z-50 pt-1 md:pt-6 w-full md:w-auto px-2 sm:px-4 md:px-0",
                className,
            )}
        >
            <div className="flex items-center gap-1 md:gap-3 bg-white/10 dark:bg-black/20 border border-white/10 backdrop-blur-xl py-0 md:py-1 px-0.5 md:px-1 rounded-full shadow-2xl w-auto mx-auto overflow-x-auto md:overflow-visible justify-center md:justify-start transition-colors duration-500">

                {/* Left Side Slot (Logo) */}
                {leftElement && (
                    <div className="flex items-center shrink-0">
                        {leftElement}
                    </div>
                )}

                {/* Navigation Items */}
                <div className="flex items-center gap-0.5 md:gap-3 md:flex-1 justify-center md:justify-start shrink min-w-0">
                    {items.map((item) => {
                        const Icon = item.icon
                        const isActive = activeTab === item.name

                        return (
                            <a
                                key={item.name}
                                href={item.url}
                                onClick={() => setActiveTab(item.name)}
                                className={cn(
                                    "relative cursor-pointer transition-colors flex items-center justify-center rounded-full shrink-0",
                                    "text-black/70 dark:text-white/70 hover:text-blue dark:hover:text-blue",
                                    isActive && "text-blue dark:text-blue",
                                    "md:px-6 md:py-2 text-sm font-semibold md:gap-2",
                                    "p-1 md:p-2" // Mobile icon padding further reduced
                                )}
                                title={item.name}
                            >
                                <span className="hidden md:inline">{item.name}</span>
                                <Icon size={16} strokeWidth={2.5} className="md:hidden" />

                                {isActive && (
                                    <motion.div
                                        layoutId="lamp"
                                        className="absolute inset-0 w-full bg-blue/5 rounded-full -z-10"
                                        initial={false}
                                        transition={{
                                            type: "spring", stiffness: 300, damping: 30
                                        }}
                                    >
                                        <div className="absolute -top-1 md:-top-2 left-1/2 -translate-x-1/2 w-6 md:w-8 h-1 bg-blue rounded-t-full">
                                            <div className="absolute w-10 md:w-12 h-6 bg-blue/20 rounded-full blur-md -top-2 -left-2" />
                                            <div className="absolute w-6 md:w-8 h-6 bg-blue/20 rounded-full blur-md -top-1" />
                                            <div className="absolute w-3 md:w-4 h-3 md:h-4 bg-blue/20 rounded-full blur-sm top-0 left-1.5 md:left-2" />
                                        </div>
                                    </motion.div>
                                )}
                            </a>
                        )
                    })}
                </div>

                {/* Right Side Slot (Theme Toggle) */}
                {rightElement && (
                    <div className="flex items-center pr-0 md:pr-2 shrink-0">
                        {rightElement}
                    </div>
                )}
            </div>
        </div>
    )
}
